import { Status } from '../services/cases/types/ICase';

type CaseStatus = Status | string | null | undefined;

export type StatusColorStyles = {
  color: string;
  backgroundColor: string;
};

// Case-status chip colors follow the design system's token file, which is the
// declared source of truth (it cites the Figma library nodes it was generated
// from). The rendered prototype hard-codes a handful of chips that disagree with
// it — warning, error and indigo — and the token value wins in each case.
//
// Two chips have no token to point at: `New`, and the background behind
// `In progress`. Those keep the value the prototype renders rather than being
// mapped onto a token that was never meant for them.
//
// Token names are in the comments so these stay traceable once the values move
// onto CSS custom properties.
export const getColorForStatus = (status: CaseStatus): string => {
  switch (status) {
    // No design-system token yet; matches the multi-stage "upcoming" pill
    // ($ms-navy-70 in multiStage.scss) so the two surfaces agree.
    case Status.Upcoming:
      return 'rgba(13, 32, 53, 0.7)';
    case Status.New:
      return '#143354'; // primary-dark
    case Status.AcceptedCareAdvisor:
      return '#01579b'; // info-dark
    case Status.AcceptedProvider:
      return '#e65100'; // warning-dark
    case Status.InProgressProvider:
      return '#4a148c'; // purple-dark
    case Status.InProgressCareAdvisor:
      return '#2e3a96'; // indigo
    case Status.CompletedByProvider:
    case Status.Completed:
      return '#1b5e20'; // success-dark
    case Status.Canceled:
      return '#c62828'; // error-dark
    default:
      return '#143354'; // primary-dark
  }
};

export const getBgColorForStatus = (status: CaseStatus): string => {
  switch (status) {
    // Matches the multi-stage "upcoming" pill ($ms-upcoming-bg).
    case Status.Upcoming:
      return '#f0f2f5';
    case Status.New:
      return '#c2d9f1'; // no token
    case Status.AcceptedCareAdvisor:
      return '#e5f6fd'; // info-bg
    case Status.AcceptedProvider:
      return '#fff4e5'; // warning-bg
    case Status.InProgressProvider:
      return '#f3e5f5'; // secondary-bg
    case Status.InProgressCareAdvisor:
      return '#e8eaf6'; // no token
    case Status.CompletedByProvider:
    case Status.Completed:
      return '#e8f5e9'; // success-bg
    case Status.Canceled:
      return '#fdecec'; // error-bg
    default:
      return '#c2d9f1'; // no token
  }
};

export const getStatusColorStyles = (status: CaseStatus): StatusColorStyles => ({
  color: getColorForStatus(status),
  backgroundColor: getBgColorForStatus(status),
});
